import { useState, useEffect, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Search, Plus, Loader2 } from "lucide-react";
import { searchCoins, addWatchlist } from "@/lib/api";
import { toast } from "sonner";

export const AddCoinDialog = ({ open, onOpenChange, onAdded, existing }) => {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [addingId, setAddingId] = useState(null);
  const timer = useRef();

  useEffect(() => {
    if (!open) { setQ(""); setResults([]); }
  }, [open]);

  useEffect(() => {
    clearTimeout(timer.current);
    if (q.trim().length < 2) { setResults([]); return; }
    setLoading(true);
    timer.current = setTimeout(async () => {
      try { setResults(await searchCoins(q.trim())); }
      catch { setResults([]); }
      finally { setLoading(false); }
    }, 350);
    return () => clearTimeout(timer.current);
  }, [q]);

  const add = async (coin) => {
    setAddingId(coin.coingecko_id);
    try {
      const wl = await addWatchlist({
        symbol: coin.symbol, coingecko_id: coin.coingecko_id, name: coin.name, image: coin.image,
      });
      toast.success(`${coin.symbol} izleme listesine eklendi.`);
      onAdded(wl);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Eklenemedi");
    } finally { setAddingId(null); }
  };

  const existingSet = new Set((existing || []).map((c) => c.symbol));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="add-coin-dialog" className="bg-[var(--surface)] border-[var(--border)] text-[var(--text-primary)] max-w-md">
        <DialogHeader>
          <DialogTitle className="font-head text-xl">Coin Ekle</DialogTitle>
          <DialogDescription className="text-[var(--text-dim)] text-sm">
            Sembol veya isim yazarak izleme listenize coin ekleyin.
          </DialogDescription>
        </DialogHeader>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--text-faint)]" />
          <Input
            data-testid="coin-search-input"
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="örn. Cardano, ADA, Dogecoin..."
            className="pl-9 bg-[var(--app-bg)] border-[var(--border)] h-11"
          />
        </div>
        <div className="max-h-[320px] overflow-y-auto space-y-1">
          {loading && <div className="py-6 text-center text-[var(--text-faint)] text-sm"><Loader2 className="h-4 w-4 animate-spin inline mr-2" />Aranıyor...</div>}
          {!loading && q.length >= 2 && results.length === 0 && (
            <div className="py-6 text-center text-[var(--text-faint)] text-sm">Sonuç bulunamadı</div>
          )}
          {results.map((coin) => {
            const added = existingSet.has(coin.symbol);
            return (
              <div key={coin.coingecko_id} data-testid={`coin-result-${coin.symbol}`}
                className="flex items-center gap-3 rounded-md px-3 py-2.5 hover:bg-[var(--surface-hover)] transition-colors">
                {coin.image && <img src={coin.image} alt={coin.symbol} className="h-6 w-6 rounded-full" />}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{coin.name}</div>
                  <div className="text-[10px] text-[var(--text-faint)] font-mono">{coin.symbol}{coin.rank ? ` · #${coin.rank}` : ""}</div>
                </div>
                <button
                  data-testid={`add-coin-${coin.symbol}`}
                  disabled={added || addingId === coin.coingecko_id}
                  onClick={() => add(coin)}
                  className={`h-8 w-8 rounded-md flex items-center justify-center transition-colors ${
                    added ? "bg-[var(--surface-hover)] text-[var(--text-faint)]" : "bg-[#00FFA3]/15 text-[var(--pos)] hover:bg-[var(--pos)] hover:text-black"
                  }`}
                >
                  {addingId === coin.coingecko_id ? <Loader2 className="h-4 w-4 animate-spin" /> : added ? "✓" : <Plus className="h-4 w-4" />}
                </button>
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
};
